import React, { useState, useEffect, useCallback } from 'react';
import { automunityAPI } from '../lib/api';
import { useAuthContext } from '../hooks/AuthContext';
import './Outreach.css';

interface Team {
  team_id: number;
  name: string;
}

interface Contact {
  contact_id: number;
  team_id: number | null;
  user_id: number | null;
  full_name: string;
  email: string | null;
  title: string | null;
  is_lead: number;
  notes: string | null;
  team_name: string | null;
  total_activities: number;
  last_outreach_date: string | null;
  latest_activity_status: string | null;
}

interface Activity {
  activity_id: number;
  campaign_id: number | null;
  contact_id: number;
  performed_by: number | null;
  activity_type: string;
  status: string;
  subject: string | null;
  notes: string | null;
  response_summary: string | null;
  sentiment: string | null;
  scheduled_date: string | null;
  sent_date: string | null;
  response_date: string | null;
  follow_up_date: string | null;
  contact_name: string;
  team_name: string | null;
  campaign_name: string | null;
  performed_by_name: string | null;
}

interface Campaign {
  campaign_id: number;
  name: string;
  description: string | null;
  problem_statement: string | null;
  proposed_solution: string | null;
  target_group: string | null;
  started_by: number | null;
  ideator_user_id: number | null;
  ideator_name: string | null;
  ideator_display_name: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  estimated_roi_hours: number | null;
  estimated_roi_dollars: number | null;
  estimated_cost_hours: number | null;
  estimated_cost_dollars: number | null;
  payback_months: number | null;
  resulting_automation_id: number | null;
  resulting_request_id: number | null;
  resulting_automation_name: string | null;
  resulting_request_title: string | null;
  started_by_name: string | null;
  total_activities: number;
  total_responses: number;
  total_outcomes: number;
  requests_generated: number;
  automations_generated: number;
  total_stakeholders: number;
  committed_stakeholders: number;
}

interface Outcome {
  outcome_id: number;
  activity_id: number | null;
  contact_id: number | null;
  campaign_id: number | null;
  outcome_type: string;
  request_id: number | null;
  automation_id: number | null;
  description: string | null;
  estimated_value_hours: number | null;
  contact_name: string | null;
  team_name: string | null;
  campaign_name: string | null;
  automation_name: string | null;
  request_title: string | null;
}

interface Stakeholder {
  stakeholder_id: number;
  campaign_id: number;
  user_id: number | null;
  contact_id: number | null;
  full_name: string;
  role: string;
  department: string | null;
  contribution_notes: string | null;
  engagement_status: string;
  campaign_name: string | null;
  user_full_name: string | null;
  user_email: string | null;
}

interface Metrics {
  activity_stats: {
    total_outreach_attempts: number;
    total_responses: number;
    total_no_response: number;
    positive_responses: number;
    negative_responses: number;
    unique_contacts_reached: number;
    unique_teams_reached: number;
  };
  conversion_stats: {
    total_outcomes: number;
    requests_created: number;
    automations_created: number;
    meetings_scheduled: number;
    referrals: number;
    total_estimated_value_hours: number;
  };
  response_rate: number;
  conversion_rate: number;
  team_breakdown: Array<{ team_name: string; team_id: number; outreach_count: number; response_count: number; outcome_count: number }>;
  pending_follow_ups: Array<{ activity_id: number; follow_up_date: string; contact_name: string; team_name: string; subject: string }>;
  days: number;
}

type TabType = 'dashboard' | 'contacts' | 'activities' | 'campaigns' | 'outcomes';

const parseApiResponse = async (res: any): Promise<any[]> => {
  const data = await res.body.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray((data as any)?.records)) return (data as any).records;
  if (typeof (data as any)?.records === 'string') return JSON.parse((data as any).records);
  return [];
};

const parseApiSingle = async (res: any): Promise<any> => {
  const data = await res.body.json();
  if ((data as any)?.records && typeof (data as any).records === 'string') return JSON.parse((data as any).records);
  if ((data as any)?.records) return (data as any).records;
  return data;
};

const Outreach: React.FC = () => {
  const { canCreate, canEdit } = useAuthContext();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [teams, setTeams] = useState<Team[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [selectedCampaignDetail, setSelectedCampaignDetail] = useState<Campaign | null>(null);
  const [showStakeholderModal, setShowStakeholderModal] = useState(false);
  const [users, setUsers] = useState<Array<{ user_id: number; full_name: string; department: string | null; cwm_department: string | null; title: string | null; is_manager: number }>>([]);

  // Form states
  const [activityForm, setActivityForm] = useState({
    contact_id: 0 as number | string, campaign_id: null as number | null, activity_type: 'email',
    status: 'planned', subject: '', notes: '', response_summary: '',
    sentiment: '' as string, scheduled_date: '', sent_date: '', response_date: '', follow_up_date: ''
  });
  const [contactForm, setContactForm] = useState({
    team_id: null as number | null, full_name: '', email: '', title: '', is_lead: 0, notes: ''
  });
  const [campaignForm, setCampaignForm] = useState({
    name: '', description: '', problem_statement: '', proposed_solution: '',
    target_group: '', start_date: '', end_date: '', status: 'active',
    ideator_user_id: null as number | null, ideator_name: '',
    estimated_roi_hours: null as number | null, estimated_roi_dollars: null as number | null,
    estimated_cost_hours: null as number | null, estimated_cost_dollars: null as number | null,
    payback_months: null as number | null
  });
  const [outcomeForm, setOutcomeForm] = useState({
    activity_id: null as number | null, contact_id: null as number | null,
    campaign_id: null as number | null, outcome_type: 'request',
    request_id: null as number | null, automation_id: null as number | null,
    description: '', estimated_value_hours: null as number | null
  });
  const [stakeholderForm, setStakeholderForm] = useState({
    user_id: null as number | null, contact_id: null as number | null,
    full_name: '', role: 'stakeholder' as string, department: '',
    contribution_notes: '', engagement_status: 'identified' as string
  });

  const loadData = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      const [teamsRes, contactsRes, activitiesRes, campaignsRes, metricsRes, outcomesRes, usersRes] = await Promise.all([
        automunityAPI.getTeams(),
        automunityAPI.getOutreachContacts(selectedTeam || undefined),
        automunityAPI.getOutreachActivities(selectedCampaign ? { campaign_id: selectedCampaign } : undefined),
        automunityAPI.getOutreachCampaigns(),
        automunityAPI.getOutreachMetrics({ campaign_id: selectedCampaign || undefined }),
        automunityAPI.getOutreachOutcomes(selectedCampaign ? { campaign_id: selectedCampaign } : undefined),
        automunityAPI.getUsers()
      ]);

      setTeams(await parseApiResponse(teamsRes));
      setContacts(await parseApiResponse(contactsRes));
      setActivities(await parseApiResponse(activitiesRes));
      setCampaigns(await parseApiResponse(campaignsRes));
      const metricsData = await parseApiSingle(metricsRes);
      setMetrics(metricsData);
      setOutcomes(await parseApiResponse(outcomesRes));
      setUsers(await parseApiResponse(usersRes));
    } catch (err) {
      console.error('Error loading outreach data:', err);
    }
    setLoading(false);
  }, [selectedTeam, selectedCampaign]);

  useEffect(() => { loadData(); }, [loadData]);

  const resolveContactId = async (value: number | string): Promise<number> => {
    // If it's a plain number, it's already a contact_id
    if (typeof value === 'number') return value;
    const strVal = String(value);
    if (!strVal.startsWith('user-')) return Number(strVal);
    
    // It's a user selection — auto-create outreach contact from user
    const userId = Number(strVal.replace('user-', ''));
    const user = users.find(u => u.user_id === userId);
    if (!user) return 0;
    
    try {
      const res = await automunityAPI.createOutreachContact({
        full_name: user.full_name,
        user_id: userId,
        email: '',
        title: user.title || '',
        is_lead: 0,
        notes: 'Auto-created from user selection'
      });
      const parsed = await parseApiSingle(res);
      // Refresh contacts in background
      const contactsRes = await automunityAPI.getOutreachContacts(selectedTeam || undefined);
      setContacts(await parseApiResponse(contactsRes));
      // Return the new contact_id from the response
      if (parsed && parsed.contact_id) return parsed.contact_id;
      // Fallback: find by user_id in refreshed contacts
      const refreshed = await parseApiResponse(await automunityAPI.getOutreachContacts());
      const match = refreshed.find((c: Contact) => c.user_id === userId);
      return match ? match.contact_id : 0;
    } catch (err) {
      console.error('Error creating contact from user:', err);
      return 0;
    }
  };

  const handleCreateActivity = async () => {
    if (!activityForm.contact_id) return;
    try {
      const contactId = await resolveContactId(activityForm.contact_id);
      if (!contactId) return;
      await automunityAPI.createOutreachActivity({ ...activityForm, contact_id: contactId });
      setShowActivityModal(false);
      setActivityForm({ contact_id: 0, campaign_id: null, activity_type: 'email', status: 'planned', subject: '', notes: '', response_summary: '', sentiment: '', scheduled_date: '', sent_date: '', response_date: '', follow_up_date: '' });
      loadData(true);
    } catch (err) { console.error('Error creating activity:', err); }
  };

  const handleUpdateActivity = async () => {
    if (!editingActivity) return;
    try {
      const contactId = await resolveContactId(activityForm.contact_id);
      if (!contactId) { console.error('[Outreach] Failed to resolve contact_id'); return; }
      console.log(`[Outreach] Updating activity ${editingActivity.activity_id}`, { ...activityForm, contact_id: contactId });
      const res = await automunityAPI.updateOutreachActivity(editingActivity.activity_id, { ...activityForm, contact_id: contactId });
      console.log(`[Outreach] Update response status:`, res.statusCode);
      const resBody = await res.body.json().catch(() => null);
      console.log(`[Outreach] Update response body:`, resBody);
      setEditingActivity(null);
      setShowActivityModal(false);
      setActivityForm({ contact_id: 0, campaign_id: null, activity_type: 'email', status: 'planned', subject: '', notes: '', response_summary: '', sentiment: '', scheduled_date: '', sent_date: '', response_date: '', follow_up_date: '' });
      setTimeout(() => loadData(true), 500);
    } catch (err) { console.error('[Outreach] Error updating activity:', err); }
  };

  const handleQuickStatusUpdate = async (activityId: number, newStatus: string) => {
    try {
      const updates: any = { status: newStatus };
      if (newStatus === 'sent') updates.sent_date = new Date().toISOString().slice(0, 19).replace('T', ' ');
      if (newStatus === 'responded') updates.response_date = new Date().toISOString().slice(0, 19).replace('T', ' ');
      console.log(`[Outreach] Quick status update: activity ${activityId} -> ${newStatus}`, updates);
      const res = await automunityAPI.updateOutreachActivity(activityId, updates);
      console.log(`[Outreach] PATCH response status:`, res.statusCode);
      const resBody = await res.body.json().catch(() => null);
      console.log(`[Outreach] PATCH response body:`, resBody);
      // Optimistic update - don't background refresh yet, let user see it stick
      setActivities(prev => prev.map(a => a.activity_id === activityId ? { ...a, status: newStatus, ...updates } : a));
      // Delay background refresh to ensure DB write propagates
      setTimeout(() => loadData(true), 1000);
    } catch (err) { 
      console.error('[Outreach] Error updating status:', err);
    }
  };

  const handleCreateContact = async () => {
    if (!contactForm.full_name) return;
    try {
      await automunityAPI.createOutreachContact(contactForm);
      setShowContactModal(false);
      setContactForm({ team_id: null, full_name: '', email: '', title: '', is_lead: 0, notes: '' });
      loadData(true);
    } catch (err) { console.error('Error creating contact:', err); }
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.name) return;
    try {
      await automunityAPI.createOutreachCampaign(campaignForm);
      setShowCampaignModal(false);
      setCampaignForm({ name: '', description: '', problem_statement: '', proposed_solution: '', target_group: '', start_date: '', end_date: '', status: 'active', ideator_user_id: null, ideator_name: '', estimated_roi_hours: null, estimated_roi_dollars: null, estimated_cost_hours: null, estimated_cost_dollars: null, payback_months: null });
      loadData(true);
    } catch (err) { console.error('Error creating campaign:', err); }
  };

  const handleCreateOutcome = async () => {
    if (!outcomeForm.outcome_type) return;
    try {
      await automunityAPI.createOutreachOutcome(outcomeForm);
      setShowOutcomeModal(false);
      setOutcomeForm({ activity_id: null, contact_id: null, campaign_id: null, outcome_type: 'request', request_id: null, automation_id: null, description: '', estimated_value_hours: null });
      loadData(true);
    } catch (err) { console.error('Error creating outcome:', err); }
  };

  const handleOpenCampaignDetail = async (campaign: Campaign) => {
    setSelectedCampaignDetail(campaign);
    try {
      const res = await automunityAPI.getOutreachStakeholders(campaign.campaign_id);
      setStakeholders(await parseApiResponse(res));
    } catch (err) { console.error('Error loading stakeholders:', err); }
  };

  const handleCreateStakeholder = async () => {
    if (!selectedCampaignDetail || !stakeholderForm.full_name) return;
    try {
      await automunityAPI.createOutreachStakeholder(selectedCampaignDetail.campaign_id, stakeholderForm);
      setShowStakeholderModal(false);
      setStakeholderForm({ user_id: null, contact_id: null, full_name: '', role: 'stakeholder', department: '', contribution_notes: '', engagement_status: 'identified' });
      const res = await automunityAPI.getOutreachStakeholders(selectedCampaignDetail.campaign_id);
      setStakeholders(await parseApiResponse(res));
    } catch (err) { console.error('Error creating stakeholder:', err); }
  };

  const handleUpdateStakeholderStatus = async (stakeholderId: number, newStatus: string) => {
    if (!selectedCampaignDetail) return;
    try {
      await automunityAPI.updateOutreachStakeholder(selectedCampaignDetail.campaign_id, stakeholderId, { engagement_status: newStatus });
      const res = await automunityAPI.getOutreachStakeholders(selectedCampaignDetail.campaign_id);
      setStakeholders(await parseApiResponse(res));
    } catch (err) { console.error('Error updating stakeholder:', err); }
  };

  const openEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    setActivityForm({
      contact_id: activity.contact_id,
      campaign_id: activity.campaign_id,
      activity_type: activity.activity_type,
      status: activity.status,
      subject: activity.subject || '',
      notes: activity.notes || '',
      response_summary: activity.response_summary || '',
      sentiment: activity.sentiment || '',
      scheduled_date: activity.scheduled_date || '',
      sent_date: activity.sent_date || '',
      response_date: activity.response_date || '',
      follow_up_date: activity.follow_up_date || ''
    });
    setShowActivityModal(true);
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      planned: '#6b7280', sent: '#3b82f6', responded: '#10b981',
      no_response: '#f59e0b', follow_up_needed: '#ef4444'
    };
    return <span className="status-badge" style={{ backgroundColor: colors[status] || '#6b7280' }}>{status.replace('_', ' ')}</span>;
  };

  const getSentimentIcon = (sentiment: string | null) => {
    if (!sentiment) return null;
    const icons: Record<string, string> = { positive: '😊', neutral: '😐', negative: '😟', no_response: '🔇' };
    return <span title={sentiment}>{icons[sentiment] || ''}</span>;
  };

  if (loading) {
    return <div className="outreach-page"><div className="loading">Loading outreach data...</div></div>;
  }

  return (
    <div className="outreach-page">
      <div className="outreach-header">
        <h1>📣 Outreach Tracker</h1>
        <p>Track and quantify team outreach efforts for automation adoption</p>
      </div>

      <div className="outreach-tabs">
        {(['dashboard', 'contacts', 'activities', 'campaigns', 'outcomes'] as TabType[]).map(tab => (
          <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* DASHBOARD TAB */}
      {activeTab === 'dashboard' && metrics && (
        <div className="outreach-dashboard">
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-value">{metrics.activity_stats.total_outreach_attempts}</div>
              <div className="metric-label">Total Outreach</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.response_rate}%</div>
              <div className="metric-label">Response Rate</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.conversion_rate}%</div>
              <div className="metric-label">Conversion Rate</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.conversion_stats.requests_created}</div>
              <div className="metric-label">Requests Generated</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.conversion_stats.automations_created}</div>
              <div className="metric-label">Automations Created</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.conversion_stats.total_estimated_value_hours}h</div>
              <div className="metric-label">Estimated Hours Saved</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.activity_stats.unique_teams_reached}</div>
              <div className="metric-label">Teams Reached</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{metrics.activity_stats.unique_contacts_reached}</div>
              <div className="metric-label">Contacts Reached</div>
            </div>
          </div>

          {/* Pending Follow-ups */}
          {metrics.pending_follow_ups.length > 0 && (
            <div className="section-card">
              <h3>⏰ Pending Follow-ups</h3>
              <table className="outreach-table">
                <thead>
                  <tr><th>Contact</th><th>Team</th><th>Subject</th><th>Follow-up Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {metrics.pending_follow_ups.map(fu => (
                    <tr key={fu.activity_id}>
                      <td>{fu.contact_name}</td>
                      <td>{fu.team_name}</td>
                      <td>{fu.subject}</td>
                      <td>{new Date(fu.follow_up_date).toLocaleDateString()}</td>
                      <td>
                        <button className="btn-sm" onClick={() => handleQuickStatusUpdate(fu.activity_id, 'sent')}>Mark Sent</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Team Breakdown */}
          {metrics.team_breakdown.length > 0 && (
            <div className="section-card">
              <h3>📊 Outreach by Team</h3>
              <table className="outreach-table">
                <thead>
                  <tr><th>Team</th><th>Outreach</th><th>Responses</th><th>Outcomes</th><th>Response %</th></tr>
                </thead>
                <tbody>
                  {metrics.team_breakdown.map(tb => (
                    <tr key={tb.team_id}>
                      <td>{tb.team_name || 'Unassigned'}</td>
                      <td>{tb.outreach_count}</td>
                      <td>{tb.response_count}</td>
                      <td>{tb.outcome_count}</td>
                      <td>{tb.outreach_count > 0 ? Math.round(tb.response_count / tb.outreach_count * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CONTACTS TAB */}
      {activeTab === 'contacts' && (
        <div className="outreach-contacts">
          <div className="section-toolbar">
            <select value={selectedTeam || ''} onChange={e => setSelectedTeam(e.target.value ? Number(e.target.value) : null)}>
              <option value="">All Teams</option>
              {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
            </select>
            {canCreate('outreach') && (
              <button className="btn-primary" onClick={() => setShowContactModal(true)}>+ Add Contact</button>
            )}
          </div>
          <table className="outreach-table">
            <thead>
              <tr><th>Name</th><th>Team</th><th>Title</th><th>Email</th><th>Lead</th><th>Outreach Count</th><th>Last Outreach</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.contact_id}>
                  <td><strong>{c.full_name}</strong></td>
                  <td>{c.team_name || '—'}</td>
                  <td>{c.title || '—'}</td>
                  <td>{c.email || '—'}</td>
                  <td>{c.is_lead ? '⭐' : ''}</td>
                  <td>{c.total_activities}</td>
                  <td>{c.last_outreach_date ? new Date(c.last_outreach_date).toLocaleDateString() : 'Never'}</td>
                  <td>{c.latest_activity_status ? getStatusBadge(c.latest_activity_status) : '—'}</td>
                  <td>
                    {canCreate('outreach') && (
                      <button className="btn-sm btn-action" onClick={() => {
                        setActivityForm({ ...activityForm, contact_id: c.contact_id });
                        setShowActivityModal(true);
                      }}>📧 Reach Out</button>
                    )}
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && <tr><td colSpan={9} className="empty-state">No contacts found. Add team members to begin outreach tracking.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ACTIVITIES TAB */}
      {activeTab === 'activities' && (
        <div className="outreach-activities">
          <div className="section-toolbar">
            <select value={selectedCampaign || ''} onChange={e => setSelectedCampaign(e.target.value ? Number(e.target.value) : null)}>
              <option value="">All Campaigns</option>
              {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
            </select>
            {canCreate('outreach') && (
              <button className="btn-primary" onClick={() => { setEditingActivity(null); setShowActivityModal(true); }}>+ Log Activity</button>
            )}
          </div>
          <table className="outreach-table">
            <thead>
              <tr><th>Date</th><th>Contact</th><th>Team</th><th>Type</th><th>Subject</th><th>Status</th><th>Sentiment</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {activities.map(a => (
                <tr key={a.activity_id}>
                  <td>{a.sent_date ? new Date(a.sent_date).toLocaleDateString() : a.scheduled_date ? new Date(a.scheduled_date).toLocaleDateString() : '—'}</td>
                  <td>{a.contact_name}</td>
                  <td>{a.team_name || '—'}</td>
                  <td><span className="type-badge">{a.activity_type}</span></td>
                  <td>{a.subject || '—'}</td>
                  <td>{getStatusBadge(a.status)}</td>
                  <td>{getSentimentIcon(a.sentiment)}</td>
                  <td className="action-cell">
                    {canEdit('outreach') && a.status === 'planned' && (
                      <button className="btn-sm" onClick={() => handleQuickStatusUpdate(a.activity_id, 'sent')}>✉️ Mark Sent</button>
                    )}
                    {canEdit('outreach') && a.status === 'sent' && (
                      <button className="btn-sm" onClick={() => handleQuickStatusUpdate(a.activity_id, 'responded')}>✅ Got Response</button>
                    )}
                    {canEdit('outreach') && (
                      <button className="btn-sm" onClick={() => openEditActivity(a)}>✏️</button>
                    )}
                  </td>
                </tr>
              ))}
              {activities.length === 0 && <tr><td colSpan={8} className="empty-state">No outreach activities yet. Start reaching out to teams!</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* CAMPAIGNS TAB */}
      {activeTab === 'campaigns' && !selectedCampaignDetail && (
        <div className="outreach-campaigns">
          <div className="section-toolbar">
            {canCreate('outreach') && (
              <button className="btn-primary" onClick={() => setShowCampaignModal(true)}>+ New Campaign</button>
            )}
          </div>
          <div className="campaigns-grid">
            {campaigns.map(c => (
              <div key={c.campaign_id} className="campaign-card" onClick={() => handleOpenCampaignDetail(c)} style={{ cursor: 'pointer' }}>
                <div className="campaign-header">
                  <h3>{c.name}</h3>
                  <span className={`campaign-status status-${c.status}`}>{c.status}</span>
                </div>
                {c.problem_statement && <p className="campaign-problem">💡 {c.problem_statement}</p>}
                {!c.problem_statement && c.description && <p className="campaign-desc">{c.description}</p>}
                <div className="campaign-meta">
                  {c.ideator_display_name && <span>🧠 Ideator: {c.ideator_display_name}</span>}
                  {c.target_group && <span>🎯 {c.target_group}</span>}
                  {c.started_by_name && <span>👤 {c.started_by_name}</span>}
                  {c.start_date && <span>📅 {new Date(c.start_date).toLocaleDateString()}</span>}
                </div>
                {(c.estimated_roi_hours || c.estimated_roi_dollars) && (
                  <div className="campaign-roi">
                    {c.estimated_roi_hours && <span className="roi-tag">💰 {c.estimated_roi_hours}h/mo saved</span>}
                    {c.estimated_roi_dollars && <span className="roi-tag">${c.estimated_roi_dollars}/mo</span>}
                    {c.payback_months && <span className="roi-tag">📈 {c.payback_months}mo payback</span>}
                  </div>
                )}
                <div className="campaign-stats">
                  <div><strong>{c.total_activities}</strong> outreach</div>
                  <div><strong>{c.total_responses}</strong> responses</div>
                  <div><strong>{c.total_stakeholders}</strong> stakeholders</div>
                  <div><strong>{c.committed_stakeholders}</strong> committed</div>
                  <div><strong>{c.requests_generated}</strong> requests</div>
                  <div><strong>{c.automations_generated}</strong> automations</div>
                </div>
                {c.resulting_automation_name && (
                  <div className="campaign-result">✅ Built: {c.resulting_automation_name}</div>
                )}
              </div>
            ))}
            {campaigns.length === 0 && <div className="empty-state">No campaigns yet. Create one to organize your outreach efforts.</div>}
          </div>
        </div>
      )}

      {/* CAMPAIGN DETAIL VIEW */}
      {activeTab === 'campaigns' && selectedCampaignDetail && (
        <div className="campaign-detail">
          <button className="btn-secondary" onClick={() => setSelectedCampaignDetail(null)} style={{ marginBottom: 16 }}>← Back to Campaigns</button>
          
          <div className="campaign-detail-header">
            <h2>{selectedCampaignDetail.name}</h2>
            <span className={`campaign-status status-${selectedCampaignDetail.status}`}>{selectedCampaignDetail.status}</span>
          </div>

          {/* Problem & Solution */}
          <div className="campaign-detail-grid">
            <div className="detail-section">
              <h4>🧠 Ideator</h4>
              <p>{selectedCampaignDetail.ideator_display_name || 'Not specified'}</p>
            </div>
            <div className="detail-section">
              <h4>🎯 Target Group</h4>
              <p>{selectedCampaignDetail.target_group || 'Not specified'}</p>
            </div>
            <div className="detail-section full-width">
              <h4>💡 Problem Statement</h4>
              <p>{selectedCampaignDetail.problem_statement || 'Not defined yet'}</p>
            </div>
            <div className="detail-section full-width">
              <h4>🛠️ Proposed Solution</h4>
              <p>{selectedCampaignDetail.proposed_solution || 'Not defined yet'}</p>
            </div>
          </div>

          {/* ROI Section */}
          {(selectedCampaignDetail.estimated_roi_hours || selectedCampaignDetail.estimated_cost_hours) && (
            <div className="section-card">
              <h3>📊 Value Model</h3>
              <div className="metrics-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                {selectedCampaignDetail.estimated_roi_hours && (
                  <div className="metric-card"><div className="metric-value">{selectedCampaignDetail.estimated_roi_hours}h</div><div className="metric-label">Monthly Hours Saved</div></div>
                )}
                {selectedCampaignDetail.estimated_roi_dollars && (
                  <div className="metric-card"><div className="metric-value">${selectedCampaignDetail.estimated_roi_dollars}</div><div className="metric-label">Monthly $ Saved</div></div>
                )}
                {selectedCampaignDetail.estimated_cost_hours && (
                  <div className="metric-card"><div className="metric-value">{selectedCampaignDetail.estimated_cost_hours}h</div><div className="metric-label">Dev Hours to Build</div></div>
                )}
                {selectedCampaignDetail.estimated_cost_dollars && (
                  <div className="metric-card"><div className="metric-value">${selectedCampaignDetail.estimated_cost_dollars}</div><div className="metric-label">Cost to Build</div></div>
                )}
                {selectedCampaignDetail.payback_months && (
                  <div className="metric-card"><div className="metric-value">{selectedCampaignDetail.payback_months}</div><div className="metric-label">Months to ROI</div></div>
                )}
              </div>
            </div>
          )}

          {/* Stakeholders */}
          <div className="section-card">
            <div className="section-toolbar">
              <h3 style={{ margin: 0 }}>👥 Stakeholders & Contributors</h3>
              {canCreate('outreach') && (
                <button className="btn-primary" onClick={() => setShowStakeholderModal(true)}>+ Add Stakeholder</button>
              )}
            </div>
            <table className="outreach-table">
              <thead>
                <tr><th>Name</th><th>Role</th><th>Department</th><th>Status</th><th>Contribution</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {stakeholders.map(s => (
                  <tr key={s.stakeholder_id}>
                    <td><strong>{s.full_name}</strong></td>
                    <td><span className={`role-badge role-${s.role}`}>{s.role}</span></td>
                    <td>{s.department || '—'}</td>
                    <td><span className={`engagement-badge engagement-${s.engagement_status}`}>{s.engagement_status}</span></td>
                    <td>{s.contribution_notes || '—'}</td>
                    <td className="action-cell">
                      {canEdit('outreach') && s.engagement_status === 'identified' && (
                        <button className="btn-sm" onClick={() => handleUpdateStakeholderStatus(s.stakeholder_id, 'contacted')}>📧 Contacted</button>
                      )}
                      {canEdit('outreach') && s.engagement_status === 'contacted' && (
                        <button className="btn-sm" onClick={() => handleUpdateStakeholderStatus(s.stakeholder_id, 'engaged')}>🤝 Engaged</button>
                      )}
                      {canEdit('outreach') && s.engagement_status === 'engaged' && (
                        <button className="btn-sm" onClick={() => handleUpdateStakeholderStatus(s.stakeholder_id, 'committed')}>✅ Committed</button>
                      )}
                    </td>
                  </tr>
                ))}
                {stakeholders.length === 0 && <tr><td colSpan={6} className="empty-state">No stakeholders added yet. Add the people involved in this campaign.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Campaign Activity Summary */}
          <div className="campaign-stats-row">
            <div className="metric-card"><div className="metric-value">{selectedCampaignDetail.total_activities}</div><div className="metric-label">Outreach Activities</div></div>
            <div className="metric-card"><div className="metric-value">{selectedCampaignDetail.total_responses}</div><div className="metric-label">Responses</div></div>
            <div className="metric-card"><div className="metric-value">{selectedCampaignDetail.total_outcomes}</div><div className="metric-label">Outcomes</div></div>
          </div>
        </div>
      )}

      {/* OUTCOMES TAB */}
      {activeTab === 'outcomes' && (
        <div className="outreach-outcomes">
          <div className="section-toolbar">
            {canCreate('outreach') && (
              <button className="btn-primary" onClick={() => setShowOutcomeModal(true)}>+ Record Outcome</button>
            )}
          </div>
          <table className="outreach-table">
            <thead>
              <tr><th>Type</th><th>Contact</th><th>Team</th><th>Campaign</th><th>Linked To</th><th>Est. Value (hrs)</th><th>Description</th></tr>
            </thead>
            <tbody>
              {outcomes.map(o => (
                <tr key={o.outcome_id}>
                  <td><span className={`outcome-type type-${o.outcome_type}`}>{o.outcome_type}</span></td>
                  <td>{o.contact_name || '—'}</td>
                  <td>{o.team_name || '—'}</td>
                  <td>{o.campaign_name || '—'}</td>
                  <td>{o.automation_name || o.request_title || '—'}</td>
                  <td>{o.estimated_value_hours || '—'}</td>
                  <td>{o.description || '—'}</td>
                </tr>
              ))}
              {outcomes.length === 0 && <tr><td colSpan={7} className="empty-state">No outcomes recorded yet. Link outreach efforts to requests and automations as they arise.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ACTIVITY MODAL */}
      {showActivityModal && (
        <div className="modal-overlay" onClick={() => { setShowActivityModal(false); setEditingActivity(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editingActivity ? 'Edit Outreach Activity' : 'Log Outreach Activity'}</h2>
            <div className="modal-form">
              <label>Contact *
                <select value={activityForm.contact_id} onChange={e => setActivityForm({ ...activityForm, contact_id: e.target.value.startsWith('user-') ? e.target.value : Number(e.target.value) })}>
                  <option value={0}>Select contact...</option>
                  {[...contacts].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(c => (
                    <option key={`contact-${c.contact_id}`} value={c.contact_id}>{c.full_name}{c.team_name ? ` (${c.team_name})` : ''}</option>
                  ))}
                  {users.filter(u => !contacts.some(c => c.user_id === u.user_id)).length > 0 && (
                    <optgroup label="All Users">
                      {[...users].filter(u => !contacts.some(c => c.user_id === u.user_id)).sort((a, b) => a.full_name.localeCompare(b.full_name)).map(u => (
                        <option key={`user-${u.user_id}`} value={`user-${u.user_id}`}>
                          {u.full_name}{u.cwm_department ? ` — ${u.cwm_department}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </label>
              <label>Campaign
                <select value={activityForm.campaign_id || ''} onChange={e => setActivityForm({ ...activityForm, campaign_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">No campaign</option>
                  {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
                </select>
              </label>
              <label>Type
                <select value={activityForm.activity_type} onChange={e => setActivityForm({ ...activityForm, activity_type: e.target.value })}>
                  <option value="email">Email</option>
                  <option value="call">Call</option>
                  <option value="meeting">Meeting</option>
                  <option value="teams_message">Teams Message</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>Status
                <select value={activityForm.status} onChange={e => setActivityForm({ ...activityForm, status: e.target.value })}>
                  <option value="planned">Planned</option>
                  <option value="sent">Sent</option>
                  <option value="responded">Responded</option>
                  <option value="no_response">No Response</option>
                  <option value="follow_up_needed">Follow-up Needed</option>
                </select>
              </label>

              <label>Subject
                <input type="text" value={activityForm.subject} onChange={e => setActivityForm({ ...activityForm, subject: e.target.value })} placeholder="Email subject or brief description" />
              </label>
              <label>Notes
                <textarea value={activityForm.notes} onChange={e => setActivityForm({ ...activityForm, notes: e.target.value })} placeholder="Details about the outreach..." rows={3} />
              </label>
              <label>Response Summary
                <textarea value={activityForm.response_summary} onChange={e => setActivityForm({ ...activityForm, response_summary: e.target.value })} placeholder="What did they say back?" rows={3} />
              </label>
              <label>Sentiment
                <select value={activityForm.sentiment} onChange={e => setActivityForm({ ...activityForm, sentiment: e.target.value })}>
                  <option value="">Not set</option>
                  <option value="positive">Positive 😊</option>
                  <option value="neutral">Neutral 😐</option>
                  <option value="negative">Negative 😟</option>
                  <option value="no_response">No Response 🔇</option>
                </select>
              </label>
              <div className="form-row">
                <label>Scheduled Date
                  <input type="datetime-local" value={activityForm.scheduled_date} onChange={e => setActivityForm({ ...activityForm, scheduled_date: e.target.value })} />
                </label>
                <label>Sent Date
                  <input type="datetime-local" value={activityForm.sent_date} onChange={e => setActivityForm({ ...activityForm, sent_date: e.target.value })} />
                </label>
              </div>
              <div className="form-row">
                <label>Response Date
                  <input type="datetime-local" value={activityForm.response_date} onChange={e => setActivityForm({ ...activityForm, response_date: e.target.value })} />
                </label>
                <label>Follow-up Date
                  <input type="datetime-local" value={activityForm.follow_up_date} onChange={e => setActivityForm({ ...activityForm, follow_up_date: e.target.value })} />
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => { setShowActivityModal(false); setEditingActivity(null); }}>Cancel</button>
              <button className="btn-primary" onClick={editingActivity ? handleUpdateActivity : handleCreateActivity}>
                {editingActivity ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTACT MODAL */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Add Outreach Contact</h2>
            <div className="modal-form">
              <label>Full Name *
                <input type="text" value={contactForm.full_name} onChange={e => setContactForm({ ...contactForm, full_name: e.target.value })} placeholder="Contact full name" />
              </label>
              <label>Team
                <select value={contactForm.team_id || ''} onChange={e => setContactForm({ ...contactForm, team_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">No team</option>
                  {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
                </select>
              </label>
              <label>Email
                <input type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} placeholder="Email address" />
              </label>
              <label>Title / Role
                <input type="text" value={contactForm.title} onChange={e => setContactForm({ ...contactForm, title: e.target.value })} placeholder="Job title or role" />
              </label>
              <label className="checkbox-label">
                <input type="checkbox" checked={contactForm.is_lead === 1} onChange={e => setContactForm({ ...contactForm, is_lead: e.target.checked ? 1 : 0 })} />
                Team Lead
              </label>
              <label>Notes
                <textarea value={contactForm.notes} onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} placeholder="Any relevant notes..." rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowContactModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateContact}>Create Contact</button>
            </div>
          </div>
        </div>
      )}

      {/* CAMPAIGN MODAL */}
      {showCampaignModal && (
        <div className="modal-overlay" onClick={() => setShowCampaignModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <h2>Create Outreach Campaign</h2>
            <div className="modal-form">
              <label>Campaign Name *
                <input type="text" value={campaignForm.name} onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })} placeholder="e.g. Invoice Review Automation for AMs" />
              </label>
              <label>Problem Statement *
                <textarea value={campaignForm.problem_statement} onChange={e => setCampaignForm({ ...campaignForm, problem_statement: e.target.value })} placeholder="What pain point are we addressing? e.g. AMs spend hours reviewing time entries on every invoice to check coverage..." rows={3} />
              </label>
              <label>Proposed Solution
                <textarea value={campaignForm.proposed_solution} onChange={e => setCampaignForm({ ...campaignForm, proposed_solution: e.target.value })} placeholder="What automation/tool could help? e.g. A tool that cross-references time entries with client agreements..." rows={3} />
              </label>
              <label>Target Group
                <input type="text" value={campaignForm.target_group} onChange={e => setCampaignForm({ ...campaignForm, target_group: e.target.value })} placeholder="e.g. Account Managers" />
              </label>
              <label>Description
                <textarea value={campaignForm.description} onChange={e => setCampaignForm({ ...campaignForm, description: e.target.value })} placeholder="Additional context..." rows={2} />
              </label>

              <h3 style={{ margin: '12px 0 4px', fontSize: '0.95rem' }}>🧠 Ideator (who surfaced this idea?)</h3>
              <div className="form-row">
                <label>Ideator (from users)
                  <select value={campaignForm.ideator_user_id || ''} onChange={e => setCampaignForm({ ...campaignForm, ideator_user_id: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">Select user...</option>
                    {[...users].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(u => <option key={u.user_id} value={u.user_id}>{u.full_name}{u.cwm_department ? ` (${u.cwm_department})` : ''}</option>)}
                  </select>
                </label>
                <label>Or type name
                  <input type="text" value={campaignForm.ideator_name} onChange={e => setCampaignForm({ ...campaignForm, ideator_name: e.target.value })} placeholder="Name if not in system" />
                </label>
              </div>

              <h3 style={{ margin: '12px 0 4px', fontSize: '0.95rem' }}>📊 Value Estimates</h3>
              <div className="form-row">
                <label>Hours Saved / Month
                  <input type="number" step="0.5" value={campaignForm.estimated_roi_hours || ''} onChange={e => setCampaignForm({ ...campaignForm, estimated_roi_hours: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 20" />
                </label>
                <label>$ Saved / Month
                  <input type="number" step="0.01" value={campaignForm.estimated_roi_dollars || ''} onChange={e => setCampaignForm({ ...campaignForm, estimated_roi_dollars: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 3000" />
                </label>
              </div>
              <div className="form-row">
                <label>Dev Hours to Build
                  <input type="number" step="0.5" value={campaignForm.estimated_cost_hours || ''} onChange={e => setCampaignForm({ ...campaignForm, estimated_cost_hours: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 40" />
                </label>
                <label>Cost to Build ($)
                  <input type="number" step="0.01" value={campaignForm.estimated_cost_dollars || ''} onChange={e => setCampaignForm({ ...campaignForm, estimated_cost_dollars: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 6000" />
                </label>
              </div>
              <label>Payback Period (months)
                <input type="number" step="0.1" value={campaignForm.payback_months || ''} onChange={e => setCampaignForm({ ...campaignForm, payback_months: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 2" />
              </label>

              <h3 style={{ margin: '12px 0 4px', fontSize: '0.95rem' }}>📅 Timeline</h3>
              <div className="form-row">
                <label>Start Date
                  <input type="date" value={campaignForm.start_date} onChange={e => setCampaignForm({ ...campaignForm, start_date: e.target.value })} />
                </label>
                <label>End Date
                  <input type="date" value={campaignForm.end_date} onChange={e => setCampaignForm({ ...campaignForm, end_date: e.target.value })} />
                </label>
              </div>
              <label>Status
                <select value={campaignForm.status} onChange={e => setCampaignForm({ ...campaignForm, status: e.target.value })}>
                  <option value="planning">Planning</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCampaignModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateCampaign}>Create Campaign</button>
            </div>
          </div>
        </div>
      )}

      {/* OUTCOME MODAL */}
      {showOutcomeModal && (
        <div className="modal-overlay" onClick={() => setShowOutcomeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Record Outcome</h2>
            <div className="modal-form">
              <label>Outcome Type *
                <select value={outcomeForm.outcome_type} onChange={e => setOutcomeForm({ ...outcomeForm, outcome_type: e.target.value })}>
                  <option value="request">Request Created</option>
                  <option value="automation">Automation Built</option>
                  <option value="meeting_scheduled">Meeting Scheduled</option>
                  <option value="referral">Referral Given</option>
                  <option value="declined">Declined</option>
                </select>
              </label>
              <label>Contact
                <select value={outcomeForm.contact_id || ''} onChange={e => setOutcomeForm({ ...outcomeForm, contact_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">Select contact...</option>
                  {[...contacts].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(c => (
                    <option key={`contact-${c.contact_id}`} value={c.contact_id}>{c.full_name}{c.team_name ? ` (${c.team_name})` : ''}</option>
                  ))}
                  {users.filter(u => !contacts.some(c => c.user_id === u.user_id)).length > 0 && (
                    <optgroup label="All Users">
                      {[...users].filter(u => !contacts.some(c => c.user_id === u.user_id)).sort((a, b) => a.full_name.localeCompare(b.full_name)).map(u => (
                        <option key={`user-${u.user_id}`} value={u.user_id}>
                          {u.full_name}{u.cwm_department ? ` — ${u.cwm_department}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </label>
              <label>Campaign
                <select value={outcomeForm.campaign_id || ''} onChange={e => setOutcomeForm({ ...outcomeForm, campaign_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">No campaign</option>
                  {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
                </select>
              </label>
              <label>Request ID (if applicable)
                <input type="number" value={outcomeForm.request_id || ''} onChange={e => setOutcomeForm({ ...outcomeForm, request_id: e.target.value ? Number(e.target.value) : null })} placeholder="Automunity request ID" />
              </label>
              <label>Automation ID (if applicable)
                <input type="number" value={outcomeForm.automation_id || ''} onChange={e => setOutcomeForm({ ...outcomeForm, automation_id: e.target.value ? Number(e.target.value) : null })} placeholder="Automunity automation ID" />
              </label>
              <label>Estimated Value (hours saved)
                <input type="number" step="0.5" value={outcomeForm.estimated_value_hours || ''} onChange={e => setOutcomeForm({ ...outcomeForm, estimated_value_hours: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 40" />
              </label>
              <label>Description
                <textarea value={outcomeForm.description} onChange={e => setOutcomeForm({ ...outcomeForm, description: e.target.value })} placeholder="Describe the outcome..." rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowOutcomeModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateOutcome}>Record Outcome</button>
            </div>
          </div>
        </div>
      )}

      {/* STAKEHOLDER MODAL */}
      {showStakeholderModal && (
        <div className="modal-overlay" onClick={() => setShowStakeholderModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Add Stakeholder</h2>
            <div className="modal-form">
              <label>Select from Users
                <select value={stakeholderForm.user_id || ''} onChange={e => {
                  const uid = e.target.value ? Number(e.target.value) : null;
                  const user = users.find(u => u.user_id === uid);
                  setStakeholderForm({ ...stakeholderForm, user_id: uid, full_name: user?.full_name || stakeholderForm.full_name, department: user?.department || stakeholderForm.department });
                }}>
                  <option value="">Or type name below...</option>
                  {[...users].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(u => <option key={u.user_id} value={u.user_id}>{u.full_name}{u.cwm_department ? ` (${u.cwm_department})` : ''}</option>)}
                </select>
              </label>
              <label>Full Name *
                <input type="text" value={stakeholderForm.full_name} onChange={e => setStakeholderForm({ ...stakeholderForm, full_name: e.target.value })} placeholder="Person's name" />
              </label>
              <label>Role *
                <select value={stakeholderForm.role} onChange={e => setStakeholderForm({ ...stakeholderForm, role: e.target.value })}>
                  <option value="ideator">Ideator (surfaced the idea)</option>
                  <option value="champion">Champion (driving adoption)</option>
                  <option value="contributor">Contributor (providing input)</option>
                  <option value="beneficiary">Beneficiary (will use it)</option>
                  <option value="approver">Approver (decision maker)</option>
                  <option value="stakeholder">Stakeholder (general)</option>
                </select>
              </label>
              <label>Department
                <input type="text" value={stakeholderForm.department} onChange={e => setStakeholderForm({ ...stakeholderForm, department: e.target.value })} placeholder="e.g. Account Management" />
              </label>
              <label>Engagement Status
                <select value={stakeholderForm.engagement_status} onChange={e => setStakeholderForm({ ...stakeholderForm, engagement_status: e.target.value })}>
                  <option value="identified">Identified</option>
                  <option value="contacted">Contacted</option>
                  <option value="engaged">Engaged</option>
                  <option value="committed">Committed</option>
                  <option value="declined">Declined</option>
                </select>
              </label>
              <label>Contribution Notes
                <textarea value={stakeholderForm.contribution_notes} onChange={e => setStakeholderForm({ ...stakeholderForm, contribution_notes: e.target.value })} placeholder="What did/will they contribute to this project?" rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowStakeholderModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateStakeholder}>Add Stakeholder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Outreach;
